/**
 * StudioAddToProductBridge — flag-gated entry point for assigning the
 * Studio-generated session images onto a product.
 *
 * Flag ON  → new unified AddToProductOverlay (no `texts` → overlay opens
 *            directly in the images view, tab bar hidden).
 * Flag OFF → legacy ProductAssignmentSlideOver (byte-identical behaviour
 *            vs. the pre-PR StudioSingleShot call). The legacy import is
 *            intentionally kept — PR7 removes it once the flag is rolled out.
 *
 * Default: flag OFF → zero user-visible change.
 */

import { useTranslation } from 'react-i18next'
import { AddToProductOverlay } from '@/components/v2/product/add-to-product'
import { useUnifiedAddToProductFlag } from '@/hooks/useUnifiedAddToProductFlag'
import {
  ProductAssignmentSlideOver,
  type GeneratedImage,
} from '@/components/studio'

export interface StudioAddToProductBridgeProps {
  open: boolean
  product: { id: string; name: string; sku?: string }
  images: { id: string; s3Url?: string; width?: number; height?: number }[]
  onClose: () => void
  onComplete?: () => void
}

export function StudioAddToProductBridge({
  open,
  product,
  images,
  onClose,
  onComplete,
}: StudioAddToProductBridgeProps) {
  const { t } = useTranslation()
  const flagEnabled = useUnifiedAddToProductFlag()

  if (!open) return null

  if (flagEnabled) {
    return (
      <AddToProductOverlay
        payload={{
          source: 'studio',
          title: t('addToProduct.buttonOpen'),
          // g.id is generated_images.id (Studio's 'assetId' field is a historical misnomer — PR0 Annahme 3)
          images: images.map((g) => ({
            id: g.id,
            url: g.s3Url ?? `/image/${g.id}`,
            width: g.width,
            height: g.height,
            kind: 'generated' as const,
          })),
          preselect: { productId: product.id },
        }}
        onClose={onClose}
        onComplete={onComplete}
      />
    )
  }

  // g.id is generated_images.id (Studio's 'assetId' field is a historical misnomer — PR0 Annahme 3)
  const legacyImages: GeneratedImage[] = images.map((g) => ({
    assetId: g.id,
    s3Url: g.s3Url,
    width: g.width ?? 1024,
    height: g.height ?? 1024,
  }))

  return (
    <ProductAssignmentSlideOver
      isOpen={open}
      onClose={onClose}
      productId={product.id}
      productName={product.name}
      productSku={product.sku}
      generatedImages={legacyImages}
      sessionInfo={{ timestamp: new Date(), promptSummary: 'KI-Generierung' }}
      onSaveComplete={onComplete}
    />
  )
}
